import { BookingTable } from "@/app/Dashboard/Components/BookingTable";
import { FullPageScreen } from "@/lib/Components/Screens/FullPageScreen";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { useMatch, useNavigate } from "@tanstack/react-router";
import { BookingStatus } from "@/gql/graphql";
import { getBookingCancelledStatuses } from "@/app/Bookings/Utils/getBookingCancelledStatuses";
import { bookingStatusCountsQuery } from "@/app/Bookings/GraphQL/bookingStatusCountsQuery";
import {
  Navbar,
  NavbarItem,
  NavbarSection,
} from "@/components/catalyst/navbar";
import { Badge, BadgeColor } from "@/components/catalyst/badge";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useTranslations } from "use-intl";
import {
  bookingTableSearchFromState,
  bookingTableStateFromSearch,
  parseBookingTableSearch,
} from "@/app/Bookings/Utils/bookingTableSearchParams";
import { BookingTableState } from "@/app/Bookings/Utils/bookingTableState";
import { useSearchBackedState } from "@/lib/Hooks/useSearchBackedState";

export function BookingTableScreen() {
  const navigate = useNavigate();
  const { search } = useMatch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/bookings/",
  });

  const [state, setState] = useSearchBackedState({
    search,
    parseSearch: parseBookingTableSearch,
    fromSearch: bookingTableStateFromSearch,
    writeSearch: (newState, { replace }) => {
      navigate({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/bookings",
        params: (prev) => prev,
        search: bookingTableSearchFromState(newState),
        replace,
      });
    },
  });

  return (
    <FullPageScreen>
      <Header state={state} onStateChange={setState} />
      <BookingTable id="bookings" state={state} onStateChange={setState} />
    </FullPageScreen>
  );
}

type Filter = {
  labelKey: keyof typeof import("@/app/Bookings/Messages/bookingMessages").bookingMessages;
  statuses: BookingStatus[];
  badgeColor?: BadgeColor;
  hideBadge?: boolean;
  isAdmin?: boolean;
};

const filters: Filter[] = [
  {
    labelKey: "all",
    statuses: [],
  },
  {
    labelKey: "pending",
    statuses: [
      BookingStatus.AwaitingConfirmation,
      BookingStatus.Vip,
      BookingStatus.Accepted,
    ],
    badgeColor: "yellow",
  },
  {
    labelKey: "confirmed",
    statuses: [BookingStatus.Confirmed],
    badgeColor: "green",
  },
  {
    labelKey: "completed",
    statuses: [BookingStatus.Completed],
    hideBadge: true,
  },
  {
    labelKey: "cancelled",
    statuses: getBookingCancelledStatuses(),
    hideBadge: true,
  },

  {
    labelKey: "abandonedCheckout",
    statuses: [BookingStatus.AwaitingPayment],
    hideBadge: true,
    isAdmin: true,
  },
];

function Header({
  state,
  onStateChange,
}: {
  state: BookingTableState;
  onStateChange: (state: BookingTableState) => void;
}) {
  "use no memo";

  const t = useTranslations("booking");
  const { isAdmin } = useTenant();
  const { supplierId } = useGlobalSupplier();
  const { data } = useGqlQuery(bookingStatusCountsQuery, {
    supplierId: supplierId ?? undefined,
  });

  return (
    <div className="relative border-gray-200 sm:border-b sm:pb-0">
      <h3 className="text-base font-semibold leading-6 text-gray-900">
        {t("bookings")}
      </h3>
      <Navbar className="-mb-px">
        <NavbarSection className="max-lg:hidden">
          {filters.map(
            (
              {
                labelKey,
                statuses,
                hideBadge,
                isAdmin: onlyShowForAdmin,
                badgeColor,
              },
              idx,
            ) => {
              if (onlyShowForAdmin && !isAdmin) {
                return null;
              }

              const count = statuses.reduce((acc, status) => {
                return (
                  acc +
                  (data?.bookingStatusCounts.find(
                    (counts) => counts.status === status,
                  )?.count ?? 0)
                );
              }, 0);

              return (
                <NavbarItem
                  key={idx}
                  current={haveSameStatuses(state.statuses, statuses)}
                  onClick={() => onStateChange({ ...state, statuses, page: 1 })}
                >
                  {t(labelKey)}

                  {count && !hideBadge ? (
                    <Badge className="py-0" color={badgeColor}>
                      {count}
                    </Badge>
                  ) : null}
                </NavbarItem>
              );
            },
          )}
        </NavbarSection>
      </Navbar>
    </div>
  );
}

function haveSameStatuses(a: BookingStatus[], b: BookingStatus[]) {
  return a.length === b.length && a.every((status) => b.includes(status));
}
